import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './CourseCardPage.css';
import { useData } from '../contexts/DataContext';
import EventCard from '../components/event/EventCard';

/**
 * CourseCardPage - immersive zoomed view expanding on CourseCard.
 * Shows more upcoming events as EventCards & assignments with richer metadata.
 */
export default function CourseCardPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { getEndeavor, getEndeavorEvents, getFilteredAssignments, getAssignmentsByEvent } = useData();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [course, setCourse] = useState(null);
  const [events, setEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [eventAssignments, setEventAssignments] = useState({});
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Responsive deliverables limit (must be before any early returns)
  const [deliverableLimit, setDeliverableLimit] = useState(999);
  useEffect(() => {
    function calcLimit(h){
      if(h < 520) return 4; // very small
      if(h < 600) return 6;
      if(h < 680) return 8;
      if(h < 760) return 10;
      if(h < 840) return 12;
      return 16; // default large screens show more but still capped for perf
    }
    const apply = () => setDeliverableLimit(calcLimit(window.innerHeight||0));
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  // Pagination state for EventCards (window of 4 in 2x2) MUST be declared before any early returns
  const [eventPage, setEventPage] = useState(0);
  const EVENTS_PER_PAGE = 4; // 2x2 grid
  const pagedEvents = useMemo(() => {
    const start = eventPage * 2; // overlapping stride of 2 (adjust if non-overlap wanted)
    return events.slice(start, start + EVENTS_PER_PAGE);
  }, [events, eventPage]);
  const canPageUp = eventPage > 0;
  const canPageDown = (eventPage * 2) + EVENTS_PER_PAGE < events.length;
  const pageUp = () => setEventPage(p => Math.max(0, p - 1));
  const pageDown = () => setEventPage(p => ((p * 2) + EVENTS_PER_PAGE < events.length ? p + 1 : p));

  const courseIdNum = useMemo(() => Number(courseId), [courseId]);

  const pickEventDate = useCallback((e) => {
    return e.startTime || e.startDate || e.startDatetime || e.startDateTime || e.eventDate || e.date || e.start || e.begin;
  }, []);
  const pickDueDate = useCallback((a) => {
    const direct = a.dueDate || a.dueDatetime || a.dueDateTime || a.due_datetime || a.due_at || a.dueAt || a.due_date || a.dateDue || a.due || a.deadline || a.dueOn;
    if (direct) return direct;
    const key = Object.keys(a).find(k => k.toLowerCase().includes('due'));
    return key ? a[key] : null;
  }, []);

  const fetchCore = useCallback(async () => {
    if (!courseIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const [cJson, rawEvents, rawAssignments] = await Promise.all([
        getEndeavor(courseIdNum),
        getEndeavorEvents(courseIdNum),
        getFilteredAssignments({ endeavorId: courseIdNum, noEvent: true, completed: false })
      ]);
      setCourse(cJson);

      const now = Date.now();
      const DAY_MS = 86400000;
      const evList = Array.isArray(rawEvents) ? rawEvents : (rawEvents?.content || []);
      let ev = evList.filter(e => {
        const dt = new Date(pickEventDate(e)).getTime();
        return !isNaN(dt) && dt >= now - DAY_MS * 2; // include 2 days back
      }).sort((a,b)=> new Date(pickEventDate(a)) - new Date(pickEventDate(b)));
      const future = ev.filter(e => new Date(pickEventDate(e)).getTime() >= now);
      const pastRecent = ev.filter(e => new Date(pickEventDate(e)).getTime() < now);
      ev = [...future, ...pastRecent].slice(0, 12);
      setEvents(ev);

      const asnList = Array.isArray(rawAssignments) ? rawAssignments : (rawAssignments?.content || []);
      let asnFiltered = asnList.filter(a => {
        const due = new Date(pickDueDate(a)).getTime();
        return !isNaN(due) && due >= now - DAY_MS * 3;
      }).sort((a,b)=> new Date(pickDueDate(a)) - new Date(pickDueDate(b)));
      const asnFuture = asnFiltered.filter(a => new Date(pickDueDate(a)).getTime() >= now);
      const asnPastRecent = asnFiltered.filter(a => new Date(pickDueDate(a)).getTime() < now);
      let asn = [...asnFuture, ...asnPastRecent].slice(0, 30);
      if (asn.length === 0 && asnList.length > 0) {
        asn = [...asnList]
          .filter(a => !!pickDueDate(a))
          .sort((a,b)=> new Date(pickDueDate(a)) - new Date(pickDueDate(b)))
          .slice(0, 30);
        if (asn.length === 0) {
          asn = asnList.slice(0, 30);
        }
      }
      setAssignments(asn);
    } catch (err) {
      setError(err.message || 'Failed to load course data');
    } finally {
      setLoading(false);
    }
  }, [courseIdNum, getEndeavor, getEndeavorEvents, getFilteredAssignments, pickEventDate, pickDueDate]);

  useEffect(()=>{ fetchCore(); }, [fetchCore]);

  // Fetch assignments for each event once events are loaded
  useEffect(() => {
    let cancelled = false;
    async function loadEventAssignments() {
      if (!events || events.length === 0) return;
      const results = {};
      await Promise.all(events.map(async ev => {
        const evId = ev.eventId || ev.id;
        if (!evId) return;
        try {
          const data = await getAssignmentsByEvent(evId);
          const list = Array.isArray(data) ? data : (data?.content || []);
            results[evId] = list;
        } catch {}
      }));
      if (!cancelled) setEventAssignments(results);
    }
    loadEventAssignments();
    return () => { cancelled = true; };
  }, [events, getAssignmentsByEvent]);

  const assignmentsByEvent = eventAssignments;

  const meta = useMemo(() => {
    if (!course) return {};
    const md = Array.isArray(course.metadata) ? course.metadata : [];
    const findVal = (label) => {
      const entry = md.find(m => (m.metadataName||'').toLowerCase() === label);
      return (entry?.value || '').toString().trim() || null;
    };
    return {
      professor: findVal('instructor name') || findVal('professor') || findVal('instructor'),
      room: findVal('course location') || findVal('room') || findVal('room number'),
      semester: findVal('semester'),
      meeting: findVal('meeting times') || findVal('schedule pattern'),
      start: course.startDate || findVal('start date'),
      end: course.endDate || findVal('end date')
    };
  }, [course]);


  if (loading) return <div className="course-card-page-container"><div>Loading…</div></div>;
  if (error) return <div className="course-card-page-container"><div className="ccp-error">{error}</div></div>;
  if (!course) return <div className="course-card-page-container"><div>No course found.</div></div>;

  const limitedDeliverables = assignments.slice(0, deliverableLimit);
  const deliverablesRemaining = assignments.length - limitedDeliverables.length;

  return (
    <div className="course-card-page-container three-row-layout">
      <div className="course-card-page-header">
        <div className="course-card-page-topline">
          <button className="zoom-back-btn btn btn-outline btn-sm" onClick={()=>navigate(-1)} title="Back">
            <span className="icon" aria-hidden="true">⤢</span>
            Back
          </button>
          <h1 className="course-card-page-title" title={course.name}>{course.name}</h1>
        </div>
        <div className="course-card-page-meta">
          {meta.professor && <span><span className="emoji" aria-hidden="true">👤</span>{meta.professor}</span>}
          {meta.room && <span><span className="emoji" aria-hidden="true">🏫</span>{meta.room}</span>}
          {meta.meeting && <span><span className="emoji" aria-hidden="true">🕒</span>{meta.meeting}</span>}
          {meta.semester && <span><span className="emoji" aria-hidden="true">📅</span>{meta.semester}</span>}
        </div>
        {course.description && (
          <div className="course-card-page-description-wrapper">
            <div className={`course-card-page-sub ${isDescriptionExpanded ? 'expanded' : 'collapsed'}`}>
              {course.description}
            </div>
            <button 
              className="course-card-page-description-toggle" 
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              aria-expanded={isDescriptionExpanded}
              aria-label={isDescriptionExpanded ? 'Collapse description' : 'Expand description'}
            >
              {isDescriptionExpanded ? '▲' : '▼'}
            </button>
          </div>
        )}
      </div>

      <div className="course-card-page-three-cols">
        {/* Col 1: Events (2/5 width) */}
        <section className="ccp-col ccp-col-events">
          <header className="ccp-row-header">
            <h2 className="ccp-row-title"><span className="icon" aria-hidden="true">📅</span> Events</h2>
            <div className="ccp-row-meta">{events.length} total</div>
            <div className="ccp-row-pagers">
              <button className="ccp-page-btn" disabled={!canPageUp} onClick={pageUp} aria-label="Previous events">▲</button>
              <button className="ccp-page-btn" disabled={!canPageDown} onClick={pageDown} aria-label="Next events">▼</button>
            </div>
          </header>
          <div className="ccp-eventcards-window">
            <div className="ccp-eventcards-grid2x2">
              {pagedEvents.length === 0 && <div className="ccp-empty">No events</div>}
              {pagedEvents.map(ev => {
                const id = ev.eventId || ev.id;
                return (
                  <EventCard
                    key={id}
                    event={ev}
                    assignments={(assignmentsByEvent[id]||[])}
                    onZoom={(eventId)=> navigate(`/event-card/${eventId}`)}
                    pickEventDate={pickEventDate}
                  />
                );
              })}
            </div>
          </div>
  </section>
  {/* Col 2: Deliverables (2/5 width) */}
  <section className="ccp-col ccp-col-deliverables">
          <header className="ccp-row-header">
            <h2 className="ccp-row-title"><span className="icon" aria-hidden="true">📝</span> Deliverables</h2>
            <div className="ccp-row-meta">{assignments.length} items</div>
          </header>
          <div className="ccp-deliverables-scroll">
            {assignments.length === 0 && <div className="ccp-empty">No deliverables</div>}
            <div className="ccp-deliverables-grid">
              {limitedDeliverables.map(asn => {
                const id = asn.assignmentId || asn.id;
                const dueRaw = asn.dueDatetime || asn.dueDateTime || asn.dueDate || asn.due_at || asn.dateDue;
                const dueLabel = (()=>{ try { const d = new Date(dueRaw); if(isNaN(d)) return '—'; return d.toLocaleDateString(undefined,{month:'short',day:'numeric'})+' '+d.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'});} catch { return '—'; } })();
                const desc = asn.description || asn.details || asn.summary || '';
                return (
                  <div key={id} className="ccp-deliverable-card" onClick={()=> id && navigate(`/assignment/${id}`)} title={asn.title || asn.name}>
                    <div className="ccp-deliverable-title">{asn.title || asn.name || 'Assignment'}</div>
                    <div className="ccp-deliverable-meta">{dueLabel}{asn.points ? ` • ${asn.points} pts` : ''}</div>
                    {desc && <div className="ccp-deliverable-desc">{desc.slice(0,120)}</div>}
                  </div>
                );
              })}
              {deliverablesRemaining > 0 && (
                <div className="ccp-deliverable-card ccp-deliverable-more" aria-label="More deliverables not shown">
                  <div className="ccp-deliverable-title">+{deliverablesRemaining} more</div>
                  <div className="ccp-deliverable-meta">Resize or zoom to view</div>
                </div>
              )}
            </div>
          </div>
        </section>
        {/* Col 3: Chat & Documents (1/5 width) */}
        <section className="ccp-col ccp-col-previews">
          <div className="ccp-preview-card" aria-label="Course Chat Preview">
            <div className="ccp-preview-header">
              <h3>Chat</h3>
              <button className="ccp-zoom-btn" onClick={()=>navigate(`/course-chat/${courseIdNum}`)}><span className="icon" aria-hidden="true">⤢</span> Zoom</button>
            </div>
            <div className="ccp-preview-body">
              <div className="ccp-preview-placeholder">Recent chat messages preview coming soon.</div>
            </div>
          </div>
          <div className="ccp-preview-card" aria-label="Course Documents Preview">
            <div className="ccp-preview-header">
              <h3>Documents</h3>
              <button className="ccp-zoom-btn" onClick={()=>navigate(`/course-documents/${courseIdNum}`)}><span className="icon" aria-hidden="true">⤢</span> Zoom</button>
            </div>
            <div className="ccp-preview-body">
              <div className="ccp-preview-placeholder">Recent documents list preview coming soon.</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
